import {
  ETrialResearchStatus,
  type TrialResearchProgress,
  type TrialResearchProgressStep,
  type TrialResearchStatus,
} from '../types';
import { trialLocationNames, trialLocationSummary } from './trial-locations';

export type ResearchFindingState = 'done' | 'running' | 'pending';

export type ResearchFinding = {
  key: string;
  state: ResearchFindingState;
  detail?: string;
};

type Translate = (key: string, opts?: Record<string, unknown>) => string;

const PROGRESS_PHASE_I18N: Record<string, string> = {
  website: 'authentication:trial_scan_phase_website',
  places: 'authentication:trial_scan_phase_locations',
  web_news: 'authentication:trial_scan_phase_news',
  socials: 'authentication:trial_scan_phase_socials',
  instagram: 'authentication:trial_scan_phase_instagram',
  extract: 'authentication:trial_scan_phase_extract',
  images: 'authentication:trial_scan_phase_images',
};

function mapStepStatus(status: TrialResearchProgressStep['status']): ResearchFindingState {
  if (status === 'done' || status === 'failed') return 'done';
  if (status === 'running') return 'running';

  return 'pending';
}

function deriveFindingsFromPreview(research: TrialResearchStatus | null, t: Translate): ResearchFinding[] {
  const preview = research?.preview;
  const finished = research?.status === ETrialResearchStatus.DONE
    || research?.status === ETrialResearchStatus.PARTIAL;
  const hasWebsite = !!preview?.name;
  const hasBrand = !!(preview?.logo_url || preview?.colors);
  const hasMission = !!preview?.mission_statement;
  const locationNames = trialLocationNames(preview?.locations);
  const hasLocations = locationNames.length > 0;

  const state = (done: boolean, runningAfter: boolean): ResearchFindingState => {
    if (done) return 'done';

    return runningAfter ? 'running' : 'pending';
  };

  return [
    { key: 'website', state: state(hasWebsite, true) },
    { key: 'brand', state: state(hasBrand, hasWebsite) },
    { key: 'mission', state: state(hasMission || finished, hasBrand) },
    {
      key: 'locations',
      state: state(finished || hasLocations, hasBrand),
      detail: hasLocations
        ? (trialLocationSummary(locationNames) ?? t('authentication:trial_scan_locations_sample'))
        : (finished ? t('authentication:trial_scan_locations_sample') : undefined),
    },
    { key: 'news', state: state(finished, hasLocations || finished) },
    { key: 'industry', state: state(finished, false) },
  ];
}

function detailForProgressStep(
  key: string,
  research: TrialResearchStatus | null,
  t: Translate,
): string | undefined {
  if (key !== 'places') return undefined;

  const locationNames = trialLocationNames(research?.preview?.locations);
  const summary = trialLocationSummary(locationNames);

  if (summary) return summary;

  const placesStep = research?.progress?.steps.find((step) => step.key === 'places');

  if (placesStep?.status === 'done' || placesStep?.status === 'failed') {
    return t('authentication:trial_scan_locations_sample');
  }

  return undefined;
}

/** Prefer GET …/trial-research `progress.steps` (node-api phased pipeline); fall back to preview heuristics. */
export function deriveResearchFindings(
  research: TrialResearchStatus | null,
  t: Translate,
): ResearchFinding[] {
  const steps = research?.progress?.steps;

  if (steps?.length) {
    return steps.map((step) => ({
      key: step.key,
      state: mapStepStatus(step.status),
      detail: detailForProgressStep(step.key, research, t),
    }));
  }

  return deriveFindingsFromPreview(research, t);
}

export function scanProgressPercentFromProgress(
  steps: TrialResearchProgressStep[],
  isDone: boolean,
) {
  if (isDone) return 100;

  const completed = steps.filter(
    (step) => step.status === 'done' || step.status === 'failed',
  ).length;

  if (!completed) return 0;

  return Math.min(92, Math.round((completed / steps.length) * 90) + 5);
}

export function scanStatusKeyFromProgress(
  progress: TrialResearchProgress | null | undefined,
  isDone: boolean,
) {
  if (isDone) return 'authentication:trial_scan_ready';

  const running = progress?.steps?.find((step) => step.status === 'running');

  if (running && PROGRESS_PHASE_I18N[running.key]) {
    return PROGRESS_PHASE_I18N[running.key];
  }

  return 'authentication:trial_scan_reading';
}

export function clampResearchPercent(value: number): number {
  return Math.min(100, Math.max(0, Math.round(value)));
}

/** Prefer API `percent`; fall back to client estimate (step counts / elapsed bar). */
export function researchScanDisplayPercent(
  research: TrialResearchStatus | null,
  isDone: boolean,
  clientFallback: number,
): number {
  if (isDone) return 100;

  const client = clampResearchPercent(clientFallback);

  if (typeof research?.percent === 'number' && Number.isFinite(research.percent)) {
    return Math.max(client, clampResearchPercent(research.percent));
  }

  return client;
}

/** Move the displayed bar toward `target` without decreasing (100ms ticks). */
export function tickMonotonicScanPercent(current: number, target: number): number {
  const goal = clampResearchPercent(target);

  if (current >= goal) return current;

  if (goal === 100) return 100;

  const delta = Math.max(1, Math.ceil((goal - current) / 4));

  return Math.min(goal, current + delta);
}

export function hasResearchPercentFromApi(research: TrialResearchStatus | null): boolean {
  return typeof research?.percent === 'number' && Number.isFinite(research.percent);
}

export function researchFindingLabelKey(findingKey: string): string {
  const legacyKeys = new Set(['website', 'brand', 'mission', 'locations', 'news', 'industry']);

  if (legacyKeys.has(findingKey)) {
    return `authentication:trial_finding_${findingKey}`;
  }

  return `authentication:trial_finding_${findingKey}`;
}
